// Session cache for offline auth. Stores the last successful `/auth/me`
// payload in IndexedDB with a hard TTL. The JWT itself lives in the
// httpOnly cookie set by the backend (max_age=7 days), so on reconnection
// the cached session is transparently revalidated against `/auth/me`.
//
// SECURITY: We never store the password nor the JWT here. Only the user
// profile object needed to render the UI + role gating. Anyone with
// physical access to the device can already read cookies via the browser
// devtools, so caching the profile is not a new leak.
const DB_NAME = "minepro-session";
const STORE = "session";
const DB_VERSION = 1;
const KEY = "current";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — matches JWT max_age

let dbPromise = null;
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("IndexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req?.result);
    tx.onerror = () => reject(tx.error);
  });
}

/** Persist the current user profile with a 7-day TTL. */
export async function saveSession(user) {
  if (!user || typeof user !== "object") return;
  try {
    const payload = {
      key: KEY,
      user: { ...user },
      saved_at: Date.now(),
      expires_at: Date.now() + TTL_MS,
    };
    await withStore("readwrite", (s) => s.put(payload));
  } catch (err) {
    console.warn("sessionCache.saveSession failed:", err?.message || err);
  }
}

/** Load the cached session if still valid; returns null otherwise. */
export async function loadSession() {
  try {
    const rec = await withStore("readonly", (s) => s.get(KEY));
    if (!rec || !rec.user) return null;
    if (Date.now() > (rec.expires_at || 0)) {
      // expired — best-effort cleanup
      try { await clearSession(); } catch (_e) { /* noop */ }
      return null;
    }
    return {
      user: rec.user,
      expires_at: rec.expires_at,
      saved_at: rec.saved_at,
    };
  } catch (err) {
    console.warn("sessionCache.loadSession failed:", err?.message || err);
    return null;
  }
}

/** Drop the cache — called on logout. */
export async function clearSession() {
  try {
    await withStore("readwrite", (s) => s.delete(KEY));
  } catch (err) {
    console.warn("sessionCache.clearSession failed:", err?.message || err);
  }
}

/** Non-async peek useful for gating pre-render UI (e.g. login form). */
export async function hasCachedSession() {
  const s = await loadSession();
  return !!s;
}
